import React, { useState, useEffect, useRef } from 'react';
import { StaffMember, CardTemplate } from '../types';
import { getNextStaffSerialNo, saveStaff, getTemplateByType } from '../services/storage';
import { drawCardOnCanvas, downloadCanvas } from '../utils/cardUtils';
import { Camera, Image as ImageIcon, Upload, Check, Loader, Download, ArrowRight, Sparkles, X, Sliders } from 'lucide-react';
import PhotoCropper from './PhotoCropper';

interface StaffRegistrationFormProps {
  role: 'faculty' | 'coordinator';
  onSuccess?: () => void;
}

const QuestionCard: React.FC<{ label: string; required?: boolean; children: React.ReactNode }> = ({ label, required, children }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm mb-4 hover:shadow-md transition-shadow border-l-4 border-l-transparent hover:border-l-emerald-500 w-full block animate-in fade-in zoom-in-95 duration-200">
    <label className="block text-base font-bold text-gray-800 mb-3 w-full">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="w-full block">{children}</div>
  </div>
);

const ImageInput = ({ value, onChange, onProcessingChange }: { value: string | null, onChange: (val: string) => void, onProcessingChange: (status: boolean) => void }) => {
  const [processing, setProcessing] = useState(false);
  const [rawImage, setRawImage] = useState<string | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    setProcessing(true);
    onProcessingChange(true);
    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        setRawImage(e.target?.result as string);
        setShowCropper(true);
        setProcessing(false);
        onProcessingChange(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      alert("Error reading file.");
      setProcessing(false);
      onProcessingChange(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const clearImage = () => {
    onChange('');
    setRawImage(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (galleryInputRef.current) galleryInputRef.current.value = '';
  };

  const aspect = 150 / 180; // Standard 3:4 vertical card photo ratio

  return (
    <div className="w-full">
       <input 
         type="file" 
         accept="image/*" 
         capture="environment"
         className="hidden" 
         ref={fileInputRef}
         onChange={handleFileChange}
       />
       <input 
         type="file" 
         accept="image/*" 
         className="hidden" 
         ref={galleryInputRef}
         onChange={handleFileChange}
       />
       
       {!value && (
         <div className="grid grid-cols-2 gap-3 w-full">
            <button 
                type="button" 
                onClick={() => fileInputRef.current?.click()}
                disabled={processing}
                className="h-32 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center text-gray-500 hover:bg-emerald-50 hover:border-emerald-400 hover:text-emerald-600 transition-all active:scale-95 cursor-pointer"
            >
                {processing ? <Loader className="animate-spin mb-2" /> : <Camera size={32} className="mb-2" />}
                <span className="font-bold text-sm">Take Photo</span>
            </button>
            <button 
                type="button" 
                onClick={() => galleryInputRef.current?.click()}
                disabled={processing}
                className="h-32 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center text-gray-500 hover:bg-blue-50 hover:border-blue-400 hover:text-blue-600 transition-all active:scale-95 cursor-pointer"
            >
                {processing ? <Loader className="animate-spin mb-2" /> : <ImageIcon size={32} className="mb-2" />}
                <span className="font-bold text-sm">Choose File</span>
            </button>
         </div>
       )}

       {value && (
         <div className="relative w-full bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm flex flex-col items-center p-3 animate-in fade-in slide-in-from-bottom-2">
            <div className="w-full aspect-[4/5] max-w-[240px] relative rounded-lg overflow-hidden border border-gray-100 shadow-inner">
               <img src={value} alt="Uploaded" className="w-full h-full object-cover" />
            </div>
            
            <div className="w-full flex justify-between items-center mt-4">
               <div className="flex items-center text-emerald-600 font-bold text-xs bg-emerald-50 px-3 py-1.5 rounded-full border border-emerald-100">
                  <Check size={14} className="mr-1.5"/> Photo Ready
               </div>
               <div className="flex gap-2">
                  <button 
                    type="button"
                    onClick={() => {
                        setRawImage(value);
                        setShowCropper(true);
                    }}
                    className="p-2 text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors cursor-pointer"
                    title="Edit/Re-crop"
                  >
                    <Sliders size={18} />
                  </button>
                  <button 
                      type="button" 
                      onClick={clearImage}
                      className="bg-red-50 text-red-600 px-4 py-2 rounded-lg text-xs hover:bg-red-100 font-black uppercase tracking-wider transition-colors cursor-pointer"
                  >
                      Remove
                  </button>
               </div>
            </div>
         </div>
       )}

       {showCropper && rawImage && (
          <PhotoCropper 
            image={rawImage}
            aspect={aspect}
            onCropComplete={(cropped) => {
              onChange(cropped);
              setShowCropper(false);
            }}
            onCancel={() => setShowCropper(false)}
          />
       )}
    </div>
  );
};

const InputClass = "w-full border border-gray-300 p-3 rounded bg-white text-gray-900 focus:ring-2 focus:ring-emerald-500 outline-none block text-base font-medium";

const StaffRegistrationForm: React.FC<StaffRegistrationFormProps> = ({ role, onSuccess }) => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [photo, setPhoto] = useState<string | null>(null);

  const [isMediaProcessing, setIsMediaProcessing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [registeredStaff, setRegisteredStaff] = useState<StaffMember | null>(null);
  const [cardTemplate, setCardTemplate] = useState<CardTemplate | null>(null);
  const [loadingTemplate, setLoadingTemplate] = useState(true);

  const canvasRef = useRef<HTMLCanvasElement>(null);

  const isFaculty = role === 'faculty';
  const roleTitleEn = isFaculty ? 'Faculty Registration' : 'Coordinator Registration';
  const roleTitleMl = isFaculty ? 'അദ്ധ്യാപക രജിസ്ട്രേഷൻ' : 'കോർഡിനേറ്റർ രജിസ്ട്രേഷൻ';

  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const template = await getTemplateByType(role);
        setCardTemplate(template);
      } catch (e) {
        console.warn("Failed to fetch staff card template:", e);
      } finally {
        setLoadingTemplate(false);
      }
    };
    fetchTemplate();
  }, [role]);

  useEffect(() => {
    if (registeredStaff && canvasRef.current && cardTemplate) {
      const mergedData = {
        serialNo: registeredStaff.serialNo,
        idNo: registeredStaff.serialNo,
        name: registeredStaff.name,
        headName: registeredStaff.name,
        phone: registeredStaff.phone,
        whatsapp: registeredStaff.whatsapp,
        housePhoto: registeredStaff.photo || '',
        categoryName: isFaculty ? 'Faculty (ഫാക്കൽറ്റി)' : 'Coordinator (കോർഡിനേറ്റർ)',
        role: role
      };
      drawCardOnCanvas(canvasRef.current, cardTemplate, mergedData);
    }
  }, [registeredStaff, cardTemplate, isFaculty, role]);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return alert("Please enter your name");
    if (!phone.trim()) return alert("Please enter your mobile number");
    if (!whatsapp.trim()) return alert("Please enter your WhatsApp number");
    if (!photo) return alert("Please upload and crop your profile photo for the ID card");

    setSubmitting(true);
    try {
      const serialNo = await getNextStaffSerialNo(role);
      const staffId = `${role}-${Date.now()}`;

      const staffData: StaffMember = {
        id: staffId,
        serialNo,
        name: name.trim(),
        phone: phone.trim(),
        whatsapp: whatsapp.trim(),
        photo: photo,
        role: role,
        submittedAt: new Date().toISOString()
      };

      await saveStaff(staffData);
      setRegisteredStaff(staffData);
    } catch (err: any) {
      console.error(err);
      alert(`Registration failed: ${err.message || 'Unknown error'}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadCard = () => {
    if (canvasRef.current && registeredStaff) {
      const sanitizedName = registeredStaff.name.replace(/\s+/g, '_');
      downloadCanvas(canvasRef.current, `ID_Card_${sanitizedName}.png`);
    }
  };

  if (registeredStaff) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4 py-12 w-full">
        <div className="max-w-xl mx-auto w-full px-4 py-12 text-center">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl border border-emerald-50">
            <div className="w-24 h-24 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-8 animate-bounce">
              <Check size={48} className="text-emerald-600" />
            </div>
            <h2 className="text-4xl font-black text-emerald-900 mb-4 tracking-tight">Registration Successful!</h2>
            <p className="text-xl font-bold text-gray-700 mb-8">Welcome to the Summer Camp team.</p>
            
            <div className="bg-emerald-50 rounded-2xl p-6 text-left space-y-4 border border-emerald-100 shadow-inner mb-6">
              <p className="font-black text-emerald-800 uppercase tracking-widest text-sm border-b border-emerald-200 pb-2">Registered Details :-</p>
              <ul className="space-y-3 text-sm font-bold text-emerald-900">
                <li className="flex gap-3">
                  <span className="text-emerald-600">•</span>
                  <span>Name: {registeredStaff.name}</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-emerald-600">•</span>
                  <span>Role: {isFaculty ? 'Faculty (അദ്ധ്യാപകൻ)' : 'Coordinator (കോർഡിനേറ്റർ)'}</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-emerald-600">•</span>
                  <span>ID No: {registeredStaff.serialNo}</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-emerald-600">•</span>
                  <span>Mobile: {registeredStaff.phone}</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-emerald-600">•</span>
                  <span>WhatsApp: {registeredStaff.whatsapp}</span>
                </li>
              </ul>
            </div>

            {/* ID Card Display */}
            {loadingTemplate ? (
              <div className="p-10 text-slate-500 flex items-center justify-center gap-2 mb-6">
                <Loader className="animate-spin text-emerald-600" /> Preparing ID Card...
              </div>
            ) : cardTemplate ? (
              <div className="w-full flex flex-col items-center space-y-4 mb-6">
                <div className="relative border-4 border-slate-100 shadow-lg rounded-xl overflow-hidden bg-slate-50 w-full max-w-sm">
                  <canvas ref={canvasRef} className="w-full h-auto block" />
                </div>
                <button
                  type="button"
                  onClick={handleDownloadCard}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-black py-4 rounded-xl shadow-lg transition active:scale-95 text-base flex justify-center items-center gap-2 cursor-pointer"
                >
                  <Download size={20} /> Download ID Card
                </button>
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 p-6 rounded-xl text-sm max-w-md mx-auto mb-6 text-left">
                <p className="font-bold mb-1">ID Card Template Pending</p>
                <p className="text-xs leading-relaxed">
                  Your ID Card template has not been designed or uploaded by the administrator yet. Once designed, the coordinator can print or download your card from the admin panel.
                </p>
              </div>
            )}
            
            <button 
              onClick={() => {
                if (onSuccess) onSuccess();
                else window.location.reload();
              }}
              className="w-full bg-emerald-800 hover:bg-emerald-900 text-white py-4 rounded-xl font-black text-lg transition-all active:scale-95 shadow-lg shadow-emerald-900/20 cursor-pointer"
            >
              CONTINUE / REGISTER ANOTHER
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-start w-full">
      {/* Header Banner matching students */}
      <div className="bg-white rounded-2xl shadow-sm p-8 mb-8 text-center border border-emerald-100 overflow-hidden relative w-full max-w-4xl">
        <div className="absolute top-0 left-0 w-full h-2 bg-emerald-600"></div>
        <h1 className="text-3xl md:text-4xl font-black text-emerald-900 mb-2 tracking-tight">
          Malayalee Samajam Summer Camp - Insight 2026
        </h1>
        <p className="text-emerald-700 font-bold text-sm uppercase tracking-widest">
          {isFaculty ? 'Faculty Registration (അദ്ധ്യാപക രജിസ്ട്രേഷൻ)' : 'Coordinator Registration (കോർഡിനേറ്റർ രജിസ്ട്രേഷൻ)'}
        </p>
      </div>

      {/* Title block matching student form step look */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6 sticky top-0 z-20 border-b border-gray-100 w-full max-w-4xl flex justify-between items-center">
         <h2 className="text-xl font-bold text-emerald-800 truncate mr-2">
            {isFaculty ? 'Faculty Registration Form' : 'Coordinator Registration Form'}
         </h2>
         <span className="text-xs font-black uppercase tracking-widest text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full border border-emerald-100">
            Insight 2026
         </span>
      </div>

      <div className="w-full max-w-4xl">
        {/* Form */}
        <form onSubmit={handleRegister} className="space-y-6 pb-32 w-full">
          
          {/* Full Name Question Card */}
          <QuestionCard label="Full Name (പൂർണ്ണ നാമം)" required>
            <input
              type="text"
              required
              placeholder="Enter your full name"
              className={InputClass}
              value={name}
              onChange={e => setName(e.target.value)}
            />
          </QuestionCard>

          {/* Mobile Number Question Card */}
          <QuestionCard label="Mobile Number (മൊബൈൽ നമ്പർ)" required>
            <input
              type="tel"
              required
              placeholder="Enter 10-digit mobile number"
              className={InputClass}
              value={phone}
              onChange={e => setPhone(e.target.value)}
            />
          </QuestionCard>

          {/* WhatsApp Number Question Card */}
          <QuestionCard label="WhatsApp Number (വാട്സ്ആപ്പ് നമ്പർ)" required>
            <input
              type="tel"
              required
              placeholder="Enter 10-digit WhatsApp number"
              className={InputClass}
              value={whatsapp}
              onChange={e => setWhatsapp(e.target.value)}
            />
          </QuestionCard>

          {/* Profile Photo Question Card */}
          <QuestionCard label="Profile Photo (പ്രൊഫൈൽ ഫോട്ടോ)" required>
            <ImageInput 
              value={photo} 
              onChange={setPhoto} 
              onProcessingChange={setIsMediaProcessing}
            />
            <p className="text-[10px] text-gray-400 mt-2">
              Please take or upload a passport-style headshot. You can crop it beautifully using our built-in cropper.
            </p>
          </QuestionCard>

          {/* Sticky footer submit button matching Student Form UX */}
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t z-20 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] pb-[env(safe-area-inset-bottom)]">
             <div className="max-w-4xl mx-auto w-full flex justify-end items-center p-3 sm:p-4">
                <button
                  type="submit"
                  disabled={submitting || isMediaProcessing}
                  className="px-6 py-3 bg-emerald-800 hover:bg-emerald-900 text-white font-black rounded-xl shadow-lg hover:shadow-emerald-900/20 transition active:scale-95 flex items-center justify-center gap-2 text-sm sm:text-base uppercase tracking-wider disabled:opacity-50 cursor-pointer"
                >
                  {submitting ? (
                    <>
                      <Loader className="animate-spin" size={18} /> REGISTERING...
                    </>
                  ) : (
                    <>
                      <Check size={18} /> SUBMIT REGISTRATION <ArrowRight size={18} className="ml-1" />
                    </>
                  )}
                </button>
             </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StaffRegistrationForm;
